// src/components/MapboxFieldDraw.jsx
import { useEffect, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import { addTerrainLayer, SATELLITE_STYLE } from "../utils/mapTerrain.js";

import "mapbox-gl/dist/mapbox-gl.css";
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";

export default function MapboxFieldDraw({
  height = 420,
  initialCenter = { lng: 30.5234, lat: 50.4501 },
  initialZoom = 10,
  valueGeoJson,
  onChangeGeoJson,
  readonly = false,
  bbox
}) {
  const mapRef     = useRef(null);
  const mapElRef   = useRef(null);
  const drawRef    = useRef(null);
  const geoCtrlRef = useRef(null);

  const token = process.env.REACT_APP_MAPBOX_TOKEN;

  const [mapErr,     setMapErr]     = useState(null);
  const [geoMsg,     setGeoMsg]     = useState(null);
  const [geoLoading, setGeoLoading] = useState(false);
  const [searchQ,    setSearchQ]    = useState("");
  const [searchRes,  setSearchRes]  = useState([]);
  const [searching,  setSearching]  = useState(false);

  /* ── geo error text ────────────────────────────────────── */
  function geoErrText(code) {
    if (code === 1) return "Доступ заблоковано — натисніть 🔒 поруч з адресою у браузері та дозвольте геолокацію";
    if (code === 2) return "Позицію не визначено. macOS: Системні параметри → Конфіденційність → Служби геолокації → увімкніть для браузера";
    if (code === 3) return "Таймаут — GPS або мережа надто повільні, спробуйте ще раз";
    return "Помилка геолокації. Перевірте дозволи браузера та налаштування macOS";
  }

  /* ── helpers ───────────────────────────────────────────── */
  function clearDraw(draw) {
    try {
      const all = draw.getAll();
      if (all?.features?.length)
        for (const f of all.features) if (f?.id) draw.delete(f.id);
    } catch {}
  }

  function getFirstPolygon(draw) {
    try {
      const data = draw.getAll();
      if (!data?.features?.length) return null;
      return data.features.find((f) => f?.geometry?.type === "Polygon") || null;
    } catch { return null; }
  }

  function fitToBbox(map, bb) {
    try {
      if (!bb) return;
      const { minLng, minLat, maxLng, maxLat } = bb;
      if (![minLng, minLat, maxLng, maxLat].every((x) => Number.isFinite(Number(x)))) return;
      map.fitBounds(
        [[Number(minLng), Number(minLat)], [Number(maxLng), Number(maxLat)]],
        { padding: 30, duration: 300 }
      );
    } catch {}
  }

  /* ── map init ──────────────────────────────────────────── */
  useEffect(() => {
    if (!token) { setMapErr("Нема REACT_APP_MAPBOX_TOKEN у .env"); return; }
    setMapErr(null);
    if (mapRef.current) return;

    mapboxgl.accessToken = token;

    const map = new mapboxgl.Map({
      container: mapElRef.current,
      style: SATELLITE_STYLE,
      center: [initialCenter.lng, initialCenter.lat],
      zoom: initialZoom,
    });

    map.addControl(new mapboxgl.NavigationControl(), "top-right");

    // GeolocateControl — trackUserLocation:true + showUserHeading:true
    // (showUserHeading requires trackUserLocation to be true)
    const geoCtrl = new mapboxgl.GeolocateControl({
      positionOptions: { enableHighAccuracy: true, timeout: 10000 },
      trackUserLocation: true,
      showUserHeading: true,
      showAccuracyCircle: true,
    });

    geoCtrl.on("geolocate", (e) => {
      const { longitude: lng, latitude: lat } = e.coords;
      map.flyTo({ center: [lng, lat], zoom: 14, duration: 1200 });
      setGeoMsg({ type: "ok", text: "Знайшли вас ✓" });
      setTimeout(() => setGeoMsg(null), 3500);
    });

    geoCtrl.on("error", (e) => {
      setGeoMsg({ type: "err", text: geoErrText(e?.code) });
      setGeoLoading(false);
    });

    geoCtrl.on("trackuserlocationstart", () => setGeoLoading(true));
    geoCtrl.on("trackuserlocationend",   () => setGeoLoading(false));

    map.addControl(geoCtrl, "top-right");
    geoCtrlRef.current = geoCtrl;

    const draw = new MapboxDraw({
      displayControlsDefault: false,
      controls: readonly ? {} : { polygon: true, trash: true },
      defaultMode: "simple_select",
    });

    map.addControl(draw, "top-left");
    mapRef.current  = map;
    drawRef.current = draw;

    const emit = () => onChangeGeoJson?.(getFirstPolygon(draw));
    map.on("draw.create", emit);
    map.on("draw.update", emit);
    map.on("draw.delete", emit);

    map.on("load", () => {
      addTerrainLayer(map);
      if (valueGeoJson?.geometry?.type === "Polygon") {
        try { clearDraw(draw); draw.add(valueGeoJson); } catch {}
      }
      setTimeout(() => {
        try { map.resize(); fitToBbox(map, bbox); } catch {}
      }, 120);
    });

    return () => {
      try { map.remove(); } catch {}
      mapRef.current    = null;
      drawRef.current   = null;
      geoCtrlRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  /* ── sync polygon ──────────────────────────────────────── */
  useEffect(() => {
    const draw = drawRef.current;
    const map  = mapRef.current;
    if (!draw || !map) return;
    if (!valueGeoJson) { clearDraw(draw); return; }
    if (valueGeoJson?.geometry?.type !== "Polygon") return;
    try {
      clearDraw(draw);
      draw.add(valueGeoJson);
      setTimeout(() => { try { map.resize(); fitToBbox(map, bbox); } catch {} }, 60);
    } catch {}
  }, [valueGeoJson, bbox]);

  /* ── address search ───────────────────────────────────── */
  async function handleSearch() {
    const q = searchQ.trim();
    if (!q || !token) return;
    setSearching(true);
    setSearchRes([]);
    try {
      const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(q)}.json?access_token=${token}&language=uk&limit=5`;
      const res = await fetch(url);
      const data = await res.json();
      setSearchRes(data.features || []);
    } catch {
      setSearchRes([]);
    } finally {
      setSearching(false);
    }
  }

  function selectSearchResult(feature) {
    const [lng, lat] = feature.center;
    mapRef.current?.flyTo({ center: [lng, lat], zoom: 14, duration: 900 });
    setSearchRes([]);
    setSearchQ("");
  }

  /* ── manual "find me" button ───────────────────────────── */
  async function handleFindMe() {
    if (!("geolocation" in navigator)) {
      setGeoMsg({ type: "err", text: "Браузер не підтримує геолокацію" });
      return;
    }

    // Перевіряємо стан дозволу перед запитом
    if ("permissions" in navigator) {
      try {
        const perm = await navigator.permissions.query({ name: "geolocation" });
        if (perm.state === "denied") {
          setGeoMsg({
            type: "err",
            text: "Доступ заблоковано — натисніть 🔒 поруч з адресою у браузері та дозвольте геолокацію",
          });
          return;
        }
      } catch {}
    }

    // Тригеримо вбудований Mapbox контрол
    const ctrl = geoCtrlRef.current;
    if (ctrl) {
      try {
        const btn = ctrl._container?.querySelector("button");
        if (btn) {
          btn.click();
          setGeoLoading(true);
          setGeoMsg({ type: "loading", text: "Визначаємо місцезнаходження…" });
          return;
        }
      } catch {}
    }

    // Fallback — прямий navigator.geolocation
    setGeoLoading(true);
    setGeoMsg({ type: "loading", text: "Визначаємо місцезнаходження…" });

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { longitude: lng, latitude: lat } = pos.coords;
        const map = mapRef.current;
        if (map) map.flyTo({ center: [lng, lat], zoom: 14, duration: 1200 });
        setGeoMsg({ type: "ok", text: "Знайшли вас ✓" });
        setGeoLoading(false);
        setTimeout(() => setGeoMsg(null), 3500);
      },
      (err) => {
        setGeoMsg({ type: "err", text: geoErrText(err.code) });
        setGeoLoading(false);
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 30000 }
    );
  }

  /* ── render ────────────────────────────────────────────── */
  return (
    <div>
      {mapErr && (
        <div style={{
          padding: "10px 14px", borderRadius: 12, marginBottom: 8,
          border: "1px solid rgba(251,113,133,0.45)",
          background: "rgba(0,0,0,0.5)", color: "#fb7185", fontSize: 13,
        }}>
          {mapErr}
        </div>
      )}

      {!readonly && (
        <div style={{ marginBottom: 10, display: "flex", flexDirection: "column", gap: 8 }}>

          {/* Пошук за адресою */}
          <div style={{ position: "relative" }}>
            <div style={{ display: "flex", gap: 6 }}>
              <input
                type="text"
                value={searchQ}
                onChange={(e) => { setSearchQ(e.target.value); if (!e.target.value.trim()) setSearchRes([]); }}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                placeholder="🔍 Пошук за адресою або містом…"
                style={{
                  flex: 1,
                  padding: "7px 12px",
                  borderRadius: 10,
                  border: "1px solid rgba(255,255,255,0.15)",
                  background: "rgba(255,255,255,0.07)",
                  color: "#fff",
                  fontSize: 13,
                  fontWeight: 700,
                  outline: "none",
                }}
              />
              <button
                type="button"
                onClick={handleSearch}
                disabled={searching || !searchQ.trim()}
                style={{
                  padding: "7px 14px",
                  borderRadius: 10,
                  border: "1px solid rgba(255,255,255,0.18)",
                  background: "rgba(255,255,255,0.10)",
                  color: "#fff",
                  fontSize: 13,
                  fontWeight: 800,
                  cursor: searching ? "default" : "pointer",
                  opacity: searching ? 0.6 : 1,
                  whiteSpace: "nowrap",
                }}
              >
                {searching ? "…" : "Знайти"}
              </button>
            </div>

            {searchRes.length > 0 && (
              <div style={{
                position: "absolute",
                top: "calc(100% + 4px)",
                left: 0,
                right: 0,
                zIndex: 999,
                borderRadius: 12,
                border: "1px solid rgba(255,255,255,0.14)",
                background: "#1a1f1a",
                overflow: "hidden",
                boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
              }}>
                {searchRes.map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => selectSearchResult(f)}
                    style={{
                      display: "block",
                      width: "100%",
                      textAlign: "left",
                      padding: "9px 14px",
                      background: "transparent",
                      border: "none",
                      borderBottom: "1px solid rgba(255,255,255,0.07)",
                      color: "rgba(255,255,255,0.88)",
                      fontSize: 13,
                      fontWeight: 700,
                      cursor: "pointer",
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = "rgba(255,255,255,0.07)"}
                    onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                  >
                    📍 {f.place_name}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Кнопка геолокації */}
          <button
            type="button"
            onClick={handleFindMe}
            disabled={geoLoading}
            style={{
              alignSelf: "flex-start",
              display: "inline-flex",
              alignItems: "center",
              gap: 7,
              padding: "7px 14px",
              borderRadius: 10,
              border: geoMsg?.type === "ok"
                ? "1px solid rgba(34,197,94,0.5)"
                : geoMsg?.type === "err"
                ? "1px solid rgba(251,113,133,0.45)"
                : "1px solid rgba(255,255,255,0.18)",
              background: geoMsg?.type === "ok"
                ? "rgba(34,197,94,0.12)"
                : geoMsg?.type === "err"
                ? "rgba(251,113,133,0.10)"
                : "rgba(255,255,255,0.08)",
              color: geoMsg?.type === "ok"
                ? "#4ade80"
                : geoMsg?.type === "err"
                ? "#fb7185"
                : "rgba(255,255,255,0.88)",
              fontSize: 13,
              fontWeight: 800,
              cursor: geoLoading ? "default" : "pointer",
              opacity: geoLoading ? 0.65 : 1,
              transition: ".15s ease",
            }}
          >
            <span style={{ fontSize: 16 }}>{geoLoading ? "⏳" : "📍"}</span>
            {geoMsg ? geoMsg.text : "Де я зараз?"}
          </button>

        </div>
      )}

      <div
        ref={mapElRef}
        style={{
          height,
          width: "100%",
          borderRadius: 16,
          overflow: "hidden",
          border: "1px solid rgba(255,255,255,0.10)",
        }}
      />
    </div>
  );
}
